import './toolbar.css';
import {
  commandState,
  isCommandAllowed,
  isCommandServed,
  presetMenuEntries,
  runCommand,
  toolCommand,
  viewerFlags,
  type CommandId,
} from '../commands';
import { fusionCandidates } from '../display';
import type { ViewerShell } from '../shell';
import { currentLeftTool, type ToolId } from '../tools';

/**
 * Popup menus (spec section 1.4 "context-menu"): the right-click menu of a viewport cell and the
 * generic popup the toolbar dropdowns reuse. Every entry calls the shared command registry.
 */

export interface MenuItem {
  label: string;
  title?: string;
  command?: CommandId;
  /** Highlighted (active tool, toggle on, preset in use). */
  active?: boolean;
  divider?: boolean;
  /** Nested entries, opened on hover or click. */
  submenu?: () => MenuItem[];
  /** Stable DOM id suffix (`cm-<id>` in the context menu, `tb-<id>-link` in toolbar menus). */
  id?: string;
}

/** A right press shorter than this opens the menu; longer holds are the zoom drag (spec section 8). */
const CONTEXT_MENU_MAX_PRESS_MS = 400;
/** Pointer travel (px) that turns a right press into a drag even if it was quick. */
const CONTEXT_MENU_MAX_TRAVEL_PX = 4;
const RIGHT_BUTTON = 2;
const VIEWPORT_MARGIN_PX = 4;

let openMenu: HTMLElement | null = null;
let closeOpenMenu: (() => void) | null = null;

/** Closes whichever popup menu is open (toolbar dropdown or context menu). */
export function closeMenus(): void {
  closeOpenMenu?.();
}

export function isMenuOpen(): boolean {
  return openMenu !== null;
}

function buildMenuList(items: MenuItem[], idPrefix: string, onPick: () => void): HTMLElement {
  const list = document.createElement('div');
  list.className = 'cmd-menu';
  list.setAttribute('role', 'menu');
  for (const item of items) {
    if (item.divider) {
      const hr = document.createElement('div');
      hr.className = 'cmd-menu-divider';
      hr.setAttribute('role', 'separator');
      list.appendChild(hr);
      continue;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cmd-menu-item';
    button.setAttribute('role', 'menuitem');
    button.textContent = item.label;
    if (item.title) button.title = item.title;
    if (item.id) button.id = `${idPrefix}${item.id}`;
    if (item.command) button.dataset.command = item.command;
    if (item.active) button.classList.add('active');
    if (item.submenu) {
      const submenu = item.submenu;
      button.classList.add('has-submenu');
      button.setAttribute('aria-haspopup', 'menu');
      const expand = () => {
        if (button.nextElementSibling?.classList.contains('cmd-submenu')) return;
        list.querySelectorAll('.cmd-submenu').forEach((el) => el.remove());
        const nested = buildMenuList(submenu(), idPrefix, onPick);
        nested.classList.add('cmd-submenu');
        button.after(nested);
      };
      button.addEventListener('mouseenter', expand);
      button.addEventListener('click', expand);
    } else {
      button.addEventListener('click', () => {
        onPick();
        if (item.command) runCommand(item.command);
      });
    }
    list.appendChild(button);
  }
  return list;
}

/**
 * Opens a popup menu at page coordinates (x, y), kept inside the window. Closes on item pick,
 * click-away, Esc, scroll of the page or a second menu opening.
 */
export function showMenu(host: HTMLElement, items: MenuItem[], x: number, y: number, options: { className?: string; idPrefix?: string; owner?: HTMLElement } = {}): HTMLElement {
  closeMenus();
  const container = document.createElement('div');
  container.className = `cmd-popup ${options.className ?? ''}`.trim();
  const close = () => {
    container.remove();
    document.removeEventListener('pointerdown', onAway, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('blur', close);
    options.owner?.setAttribute('aria-expanded', 'false');
    if (openMenu === container) {
      openMenu = null;
      closeOpenMenu = null;
    }
  };
  // A pointer press, not a mouse press: a tap on cornerstone's element synthesises no mousedown.
  const onAway = (event: PointerEvent) => {
    const target = event.target as Node | null;
    if (target && (container.contains(target) || options.owner?.contains(target))) return;
    close();
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    close();
    options.owner?.focus();
  };
  container.appendChild(buildMenuList(items, options.idPrefix ?? '', close));
  host.appendChild(container);
  const rect = container.getBoundingClientRect();
  const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(x, window.innerWidth - rect.width - VIEWPORT_MARGIN_PX));
  const top = Math.max(VIEWPORT_MARGIN_PX, Math.min(y, window.innerHeight - rect.height - VIEWPORT_MARGIN_PX));
  container.style.left = `${left}px`;
  container.style.top = `${top}px`;
  document.addEventListener('pointerdown', onAway, true);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('blur', close);
  options.owner?.setAttribute('aria-expanded', 'true');
  openMenu = container;
  closeOpenMenu = close;
  container.querySelector<HTMLButtonElement>('.cmd-menu-item')?.focus({ preventScroll: true });
  return container;
}

function toolItem(id: ToolId, label: string, title: string, key: string): MenuItem {
  return { id: key, label, title, command: toolCommand(id), active: currentLeftTool() === id };
}

/** Entries of the viewport right-click menu, in IMEXHS order (spec section 1.4). */
export function contextMenuItems(shell: ViewerShell): MenuItem[] {
  const flags = viewerFlags(shell);
  const state = commandState();
  const items: MenuItem[] = [
    { id: 'copy', label: 'Copiar', title: 'Copiar el corte en formato jpg', command: 'copy-image' },
    { id: 'presets', label: 'Ventanas predeterminadas', title: 'Ventanas predeterminadas', submenu: () => presetMenuEntries(shell) },
  ];
  if (flags.tools3d && flags.noAxialMpr) items.push({ id: 'mpr', label: 'MPR', title: '(w) Mpr', command: 'mpr' });
  if (flags.obliqueMprEnabled) items.push({ id: 'mpr-oblique', label: 'MPR Obliquo (Beta)', title: 'Mpr Obliquo', command: 'mpr-oblique' });
  const petSeries = fusionCandidates(shell, shell.activeViewport());
  if (petSeries.length > 0) {
    items.push({
      id: 'fuse',
      label: 'Fusionar serie',
      title: 'Superponer una serie PET sobre esta imagen',
      submenu: () => petSeries.map((series): MenuItem => ({
        id: `fuse-${series.seriesNumber}`,
        label: series.description || `Serie ${series.seriesNumber}`,
        title: `${series.modality} · ${series.instances.length}`,
        command: `fuse:${series.seriesInstanceUID}`,
      })),
    });
  }
  items.push(
    toolItem('window-level', 'Ventana', '(w) Cambia de ventana y de nivel, utiliza los números para moverte por las ventanas predefinidas', 'window'),
    toolItem('lines', 'Líneas', '(l) Líneas de medida', 'lines'),
    toolItem('ellipticalRoi', 'ROI', '(b) ROI Elíptico', 'roi'),
    toolItem('stack', 'Avanzar', '(s) Desplazarse por la pila de imágenes', 'stack'),
    toolItem('angles', 'Ángulos', '(a) Medir ángulos', 'angles'),
    toolItem('pan', 'Mover', '(p) Desplazar el plano de la imagen', 'pan'),
    toolItem('cobbs', "Ángulo de Cobb's", '(c) Medir los ángulos de Cobb', 'cobbs'),
    toolItem('zoom', 'Ampliar', '(z) Acercar o alejar el zoom', 'zoom'),
    toolItem('probe', 'Densidad', '(d) Buscar el valor de un píxel', 'probe'),
    toolItem('magnify', 'Lupa', '(m) Ampliar una región de la imagen', 'magnify'),
    toolItem('triangulation', 'Triangulación', '(t) Triangulación', 'triangulation'),
    toolItem('erase', 'Borrar', '(e) Borrar mediciones', 'erase'),
    { id: 'full-screen', label: 'Maximizar', title: '(Doble clic) Mostrar el visor en pantalla completa', command: 'full-screen', active: state.fullScreen },
    { id: 'clear', label: 'Limpiar', title: 'Borrar todas las mediciones', command: 'clear-all' },
    { id: 'hide-annotations', label: 'Ocultar Info. DICOM', title: '(h) Ocultar anotaciones de texto', command: 'toggle-overlay', active: !state.overlayVisible },
    { id: 'download', label: 'Descargar', title: 'Descargar una imagen con medidas', command: 'download-dialog' },
    { id: 'reset', label: 'Reiniciar Vista', title: 'Restablecer WL, zoom y posición', command: 'reset' },
    { id: 'cine', label: 'Cine', title: 'Animar imágenes', command: 'cine-toggle', active: state.cinePlaying },
  );
  if (flags.uploadEnabled && flags.stowUrl) items.push({ id: 'capture', label: 'Captura Dicom', title: 'Guardar en el servidor como captura secundaria', command: 'secondary-capture' });
  items.push({ id: 'metadata', label: 'Metadatos', title: 'Mostrar metadatos DICOM', command: 'metadata-dialog' });
  // Reporte: the IMEXHS gate, or ris serving the study report.
  if (flags.reportsModule || isCommandServed('study-report')) items.push({ id: 'report', label: 'Reporte', title: 'Abrir reporte del estudio', command: 'study-report' });
  return items.filter((item) => !item.command || isCommandAllowed(item.command));
}

function isInsideViewport(shell: ViewerShell, target: EventTarget | null): boolean {
  return target instanceof Node && shell.regions.grid.contains(target) && !(target instanceof HTMLVideoElement);
}

/** Right-click (< 400 ms, no drag) on a viewport opens the context menu at the cursor. */
export function install(shell: ViewerShell): void {
  let pressStart = 0;
  let pressX = 0;
  let pressY = 0;
  let pressed = false;
  window.addEventListener('mousedown', (event) => {
    if (event.button !== RIGHT_BUTTON || !isInsideViewport(shell, event.target)) return;
    pressed = true;
    pressStart = performance.now();
    pressX = event.clientX;
    pressY = event.clientY;
  }, true);
  window.addEventListener('mouseup', (event) => {
    if (event.button !== RIGHT_BUTTON || !pressed) return;
    pressed = false;
    const quick = performance.now() - pressStart < CONTEXT_MENU_MAX_PRESS_MS;
    const still = Math.hypot(event.clientX - pressX, event.clientY - pressY) <= CONTEXT_MENU_MAX_TRAVEL_PX;
    if (!quick || !still) return;
    showMenu(shell.regions.overlayLayer, contextMenuItems(shell), event.clientX, event.clientY, { className: 'context-menu', idPrefix: 'cm-' });
  }, true);
  shell.regions.grid.addEventListener('contextmenu', (event) => event.preventDefault());
}
